<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import ActionButton from '@/components/ui/ActionButton.vue';
import RoundedButton from '@/components/ui/RoundedButton.vue';
import { nearestAddress, type AddressResult } from '@/libs/geocoding';
import type { Place } from '@/libs/journey/places';
import { useAppStore } from '@/stores/app';

/**
 * Picks a point on the map: the map moves under a pin fixed at its centre, and the
 * address under the pin is named once the map comes to rest.
 */
const props = defineProps<{ end: 'origin' | 'destination' }>();

const emit = defineEmits<{ confirm: [place: Place]; cancel: [] }>();

/** Leaves the map at rest a moment before naming the place: no request for each nudge. */
const NAMING_DELAY_MS = 400;

const appStore = useAppStore();

const address = ref<AddressResult>();
const isNaming = ref<boolean>(false);
let namingTimeout: number | undefined;
let naming: AbortController | undefined;

watch(
  () => appStore.mapPickedPoint,
  point => {
    window.clearTimeout(namingTimeout);
    // Forgotten at once: its `finally` must not reset the state of the point that follows.
    naming?.abort();
    naming = undefined;
    address.value = undefined;
    isNaming.value = false;
    if (!point || navigator.onLine === false) return;
    isNaming.value = true;
    namingTimeout = window.setTimeout(async () => {
      // eslint-disable-next-line compat/compat -- iOS 11 is below our real support floor (plugin-legacy targets 'defaults, not IE 11'); .browserslistrc is intentionally broader.
      const request = new AbortController();
      naming = request;
      try {
        address.value = await nearestAddress(point, request.signal);
      } catch {
        // Unnamed, the point is kept all the same: « Point sur la carte ».
      } finally {
        if (naming === request) isNaming.value = false;
      }
    }, NAMING_DELAY_MS);
  },
  { immediate: true },
);

function confirm() {
  const point = appStore.mapPickedPoint;
  if (!point) return;
  // About 10 cm: the rest is float noise from unprojecting the pixel, which would end up in the URL.
  const round = (value: number) => Number(value.toFixed(6));
  emit('confirm', {
    kind: 'point',
    lat: round(point.lat),
    lon: round(point.lon),
    label: address.value?.name,
  });
}

let unregisterBackHandler: (() => void) | undefined;

onMounted(() => {
  appStore.setPickingOnMap(true);
  unregisterBackHandler = appStore.registerBackHandler(() => {
    emit('cancel');
    return true;
  });
});

onBeforeUnmount(() => {
  window.clearTimeout(namingTimeout);
  naming?.abort();
  unregisterBackHandler?.();
  appStore.setPickingOnMap(false);
});
</script>

<template>
  <div class="map-point-picker">
    <div class="map-point-picker__top">
      <RoundedButton
        :aria-label="$t('common.back')"
        class="map-point-picker__back"
        icon="arrow-left"
        size="large"
        type="outline"
        @click="emit('cancel')"
      />
      <p class="map-point-picker__title">
        {{
          $t(
            props.end === 'origin'
              ? 'itinerary.pickOnMap.titleOrigin'
              : 'itinerary.pickOnMap.titleDestination',
          )
        }}
      </p>
    </div>

    <div class="map-point-picker__panel">
      <div class="map-point-picker__place" aria-live="polite">
        <template v-if="address">
          <span class="map-point-picker__name">{{ address.name }}</span>
          <span class="map-point-picker__locality">{{ address.locality }}</span>
        </template>
        <span v-else class="map-point-picker__hint">
          {{ $t(isNaming ? 'common.loading' : 'itinerary.pickOnMap.hint') }}
        </span>
      </div>
      <ActionButton
        class="map-point-picker__confirm"
        :label="$t('itinerary.pickOnMap.confirm')"
        size="large"
        type="primary"
        :disabled="!appStore.mapPickedPoint"
        @click="confirm"
      />
    </div>
  </div>
</template>

<style lang="scss">
.map-point-picker {
  inset: 0;
  pointer-events: none;
  position: absolute;

  &__top {
    align-items: center;
    display: flex;
    gap: $spacing-3;
    left: 0;
    padding: calc(4px + var(--status-bar-band)) $spacing-4 0;
    position: absolute;
    right: 0;
    top: 0;
    z-index: 3;
  }

  &__back {
    box-shadow: $shadow-soft;
    pointer-events: auto;
  }

  &__title {
    background-color: $color-white;
    border-radius: $radius-pill;
    box-shadow: $shadow-soft;
    color: $color-neutral-highest;
    font: $label-medium;
    letter-spacing: $label-medium-spacing;
    margin: 0;
    padding: $spacing-2 $spacing-4;
  }

  &__panel {
    background-color: $color-white;
    border-radius: $radius-2xl $radius-2xl 0 0;
    bottom: 0;
    box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.08);
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: $spacing-4;
    left: 0;
    padding: $spacing-5 $spacing-4 $spacing-4;
    pointer-events: auto;
    position: absolute;
    right: 0;
    z-index: 3;
  }

  &__place {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-height: 44px;
  }

  &__name {
    color: $color-neutral-highest;
    font: $text-bold-large;
    letter-spacing: $text-bold-large-spacing;
  }

  &__locality,
  &__hint {
    color: $color-neutral-higher;
    font: $text-small;
    letter-spacing: $text-small-spacing;
  }

  &__confirm {
    width: 100%;
  }
}
</style>
